import { useEffect } from 'react';
import { isPrerender as _isPrerender } from '../utils/isPrerender';

// Fókuszolható elemek egy modálon belül (Tab-csapdához + kezdő fókuszhoz).
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

// ── Body scroll-lock (iOS Safari-biztos, ref-count-os) ───────────────────────
// Több modál egyszerre: számláló – a zárolást csak az UTOLSÓ modál oldja fel,
// és állítja vissza a scroll pozíciót. iOS Safarin az `overflow:hidden` önmagában
// NEM elég, ezért `position:fixed` + negatív `top` a bevált, regressziómentes minta.
let _lockCount = 0;
let _savedScrollY = 0;
let _savedBody = null;

// A prerender (puppeteer) a teljes document HTML-jét menti (page.content()).
// A CookieBanner prerender közben LÁTHATÓ, ezért a body inline-stílust rá-
// sütné minden statikus oldalra (fagyott görgetés hidráció előtt). A prerender
// egyedi User-Agentje alapján itt kihagyjuk a zárolást – valódi böngészőben
// (bármely modálnál) normálisan fut. A felismerés az utils/isPrerender-ből jön
// (ugyanazt az időfüggő-állapot problémát oldja a nyitvatartás-függő CTA-knál is).


const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  if (_lockCount === 0) {
    const b = document.body;
    _savedScrollY = window.scrollY || window.pageYOffset || 0;
    _savedBody = { position: b.style.position, top: b.style.top, left: b.style.left, right: b.style.right, width: b.style.width, overflow: b.style.overflow };
    b.style.position = 'fixed';
    b.style.top = `-${_savedScrollY}px`;
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
  }
  _lockCount += 1;
};

const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  _lockCount = Math.max(0, _lockCount - 1);
  if (_lockCount === 0) {
    const b = document.body;
    const s = _savedBody || {};
    b.style.position = s.position || '';
    b.style.top = s.top || '';
    b.style.left = s.left || '';
    b.style.right = s.right || '';
    b.style.width = s.width || '';
    b.style.overflow = s.overflow || '';
    _savedBody = null;
    window.scrollTo(0, _savedScrollY);
  }
};

/**
 * Modál akadálymentesítés (WAI-ARIA dialog minta) + háttér scroll-lock:
 *  - megnyíláskor a fókusz a modálba kerül, bezáráskor visszaáll a korábbi elemre,
 *  - Tab/Shift+Tab a modálon belül körbejár (fókuszcsapda),
 *  - Escape → onEscape() (ha meg van adva; pl. consent-gate-nél szándékosan nincs),
 *  - a háttér (body) nem görgethető, amíg a modál nyitva van; több modál/route-
 *    váltás/unmount esetén is mindig helyreáll a görgetés és a scroll pozíció.
 *
 * A modál konténerére tegyél `ref`-et, `role="dialog"`, `aria-modal="true"` és
 * `tabIndex={-1}` attribútumokat.
 *
 * @param {React.RefObject<HTMLElement>} containerRef a modál konténer ref-je
 * @param {Object} opts
 * @param {boolean} opts.active fut-e a kezelés (a modál látható-e)
 * @param {() => void} [opts.onEscape] Escape lenyomásra hívódik (opcionális)
 */
export default function useModalA11y(containerRef, { active = true, onEscape } = {}) {
  // Scroll-lock – KÜLÖN effekt, CSAK az `active`-tól függ, hogy az onEscape/ref
  // változása ne oldja fel és zárolja újra (ne ugráljon a scroll).
  useEffect(() => {
    if (!active || _isPrerender()) return undefined;
    lockBodyScroll();
    return unlockBodyScroll;
  }, [active]);

  // Fókuszkezelés + Escape + Tab-csapda.
  useEffect(() => {
    if (!active) return undefined;
    const node = containerRef.current;
    const prevFocused = typeof document !== 'undefined' ? document.activeElement : null;

    // Kezdő fókusz: első fókuszolható elem, különben maga a konténer.
    const focusables = node ? node.querySelectorAll(FOCUSABLE) : [];
    const initial = focusables[0] || node;
    if (initial && typeof initial.focus === 'function') initial.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (onEscape) { e.stopPropagation(); onEscape(); }
        return;
      }
      if (e.key === 'Tab' && node) {
        const f = node.querySelectorAll(FOCUSABLE);
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        // Ha a fókusz KIESETT a modálból (a modálon belüli nézetváltás
        // unmountolta a fókuszban lévő gombot → a fókusz a body-ra került),
        // a pozíció-alapú csapda átjárhatóvá válna: a Tab a modál MÖGÖTTI
        // oldalra vinne, ahonnan nincs visszaút. Ilyenkor visszahúzzuk a
        // fókuszt a modál elejére/végére.
        if (!node.contains(document.activeElement)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (prevFocused && typeof prevFocused.focus === 'function') prevFocused.focus();
    };
  }, [containerRef, active, onEscape]);
}
